import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationService } from "@/api/notificationService";
import { notificationQueryKeys } from "./notificationQueryKeys";
import type { PortalNotificationsResponseDO } from "@/types/notification";

export const useNotifications = (enabled = true, branchId?: string) => {
  return useQuery({
    queryKey: notificationQueryKeys.list(branchId),
    queryFn: notificationService.fetchNotifications,
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useUnreadNotificationsCount = (enabled = true, branchId?: string) => {
  return useQuery({
    queryKey: notificationQueryKeys.unreadCount(branchId),
    queryFn: notificationService.fetchUnreadCount,
    enabled,
    staleTime: 1000 * 30,
  });
};

export const useMarkAllNotificationsRead = (branchId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.markAllAsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.list(branchId),
      });

      const previousNotifications = queryClient.getQueryData<PortalNotificationsResponseDO>(
        notificationQueryKeys.list(branchId),
      );

      if (previousNotifications) {
        queryClient.setQueryData<PortalNotificationsResponseDO>(
          notificationQueryKeys.list(branchId),
          {
            ...previousNotifications,
            unreadCount: 0,
            notifications: previousNotifications.notifications.map(
              (notification) => ({
                ...notification,
                isRead: true,
              }),
            ),
          },
        );
      }

      queryClient.setQueryData(notificationQueryKeys.unreadCount(branchId), {
        unreadCount: 0,
      });

      return { previousNotifications };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationQueryKeys.list(branchId),
          context.previousNotifications,
        );
        queryClient.setQueryData(notificationQueryKeys.unreadCount(branchId), {
          unreadCount: context.previousNotifications.unreadCount,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
    },
  });
};
